import {
  CalendarIcon,
  GlobeAltIcon,
  MapPinIcon,
  SparklesIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const AboutCompany = () => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const companyValues = [
    {
      icon: SparklesIcon,
      title: t('aboutCompany.values.sustainability.title'),
      description: t('aboutCompany.values.sustainability.description'),
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
    {
      icon: TrophyIcon,
      title: t('aboutCompany.values.excellence.title'),
      description: t('aboutCompany.values.excellence.description'),
      color: 'from-avoprime-light to-avoprime-primary',
    },
    {
      icon: GlobeAltIcon,
      title: t('aboutCompany.values.globalMarket.title'),
      description: t('aboutCompany.values.globalMarket.description'),
      color: 'from-avoprime-secondary to-avoprime-dark',
    },
  ];

  const certifications = [
    {
      badge: t('aboutCompany.certifications.ifa.badge'),
      title: t('aboutCompany.certifications.ifa.title'),
      subtitle: t('aboutCompany.certifications.ifa.subtitle'),
      description: t('aboutCompany.certifications.ifa.description'),
      features: t('aboutCompany.certifications.ifa.features', {
        returnObjects: true,
      }) as string[],
    },
    {
      badge: t('aboutCompany.certifications.grasp.badge'),
      title: t('aboutCompany.certifications.grasp.title'),
      subtitle: t('aboutCompany.certifications.grasp.subtitle'),
      description: t('aboutCompany.certifications.grasp.description'),
      features: t('aboutCompany.certifications.grasp.features', {
        returnObjects: true,
      }) as string[],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Garantir que o vídeo funcione no Safari
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.log('Autoplay não permitido, tentando novamente...');
          // Se autoplay falhar, tenta novamente quando o usuário interagir
          const handleUserInteraction = async () => {
            try {
              await video.play();
              document.removeEventListener('click', handleUserInteraction);
              document.removeEventListener('touchstart', handleUserInteraction);
            } catch (e) {
              console.log('Erro ao reproduzir vídeo:', e);
            }
          };
          document.addEventListener('click', handleUserInteraction);
          document.addEventListener('touchstart', handleUserInteraction);
        }
      };

      // Aguarda o vídeo carregar
      video.addEventListener('loadeddata', playVideo);

      return () => {
        video.removeEventListener('loadeddata', playVideo);
      };
    }
  }, []);

  return (
    <section className='py-8 bg-gradient-to-br from-white to-avoprime-light/10'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('aboutCompany.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('aboutCompany.subtitle')}
          </p>
        </motion.div>

        {/* Company Story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className='bg-gradient-to-r from-avoprime-primary to-avoprime-secondary rounded-3xl p-8 lg:p-12 text-white mb-16'
        >
          <div className='grid lg:grid-cols-2 gap-8 items-center'>
            <div>
              <h3 className='text-3xl lg:text-4xl font-eras font-bold mb-6'>
                {t('aboutCompany.story.title')}
              </h3>
              <p className='text-lg lg:text-xl font-montserrat opacity-90 mb-6 leading-relaxed'>
                {t('aboutCompany.story.description')}
              </p>
              <div className='space-y-4'>
                <div className='flex items-center space-x-4'>
                  <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center'>
                    <MapPinIcon className='w-6 h-6 text-white' />
                  </div>
                  <div>
                    <div className='font-eras font-bold text-lg'>
                      {t('aboutCompany.story.location')}
                    </div>
                    <div className='text-sm opacity-80'>
                      {t('aboutCompany.story.locationDesc')}
                    </div>
                  </div>
                </div>
                <div className='flex items-center space-x-4'>
                  <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center'>
                    <CalendarIcon className='w-6 h-6 text-white' />
                  </div>
                  <div>
                    <div className='font-eras font-bold text-lg'>
                      {t('aboutCompany.story.founded')}
                    </div>
                    <div className='text-sm opacity-80'>
                      {t('aboutCompany.story.foundedDesc')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='relative'>
              <div className='w-full h-64 lg:h-80 rounded-2xl overflow-hidden'>
                <video
                  ref={videoRef}
                  className='w-full h-full object-cover'
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls={false}
                  preload='auto'
                  webkit-playsinline='true'
                  x5-playsinline='true'
                  x5-video-player-type='h5'
                  x5-video-player-fullscreen='true'
                >
                  <source src='/avocado-video.mp4' type='video/mp4' />
                  Seu navegador não suporta vídeos HTML5.
                </video>
              </div>
              <div className='absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl' />
            </div>
          </div>
        </motion.div>

        {/* Company Values */}
        <motion.div
          variants={containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-16'
        >
          {companyValues.map((value, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className='group relative'
            >
              <div className='bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 h-64 flex flex-col'>
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}
                >
                  <value.icon className='w-8 h-8 text-white' />
                </div>
                <h3 className='text-lg font-eras font-bold text-avoprime-primary mb-3 text-center'>
                  {value.title === 'Associados' ||
                  value.title === 'Associates' ||
                  value.title === 'Asociados' ? (
                    <a
                      href='https://abacatesdobrasil.org.br/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='hover:text-avoprime-secondary transition-colors duration-200'
                    >
                      {value.title}
                    </a>
                  ) : (
                    value.title
                  )}
                </h3>
                <p className='text-gray-600 font-montserrat text-center text-sm leading-relaxed whitespace-pre-line flex-1'>
                  {value.title === 'Associados' ||
                  value.title === 'Associates' ||
                  value.title === 'Asociados' ? (
                    <>
                      {value.title === 'Associados' && (
                        <>
                          Avoprime é associada à{' '}
                          <a
                            href='https://abacatesdobrasil.org.br/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-avoprime-primary hover:text-avoprime-secondary transition-colors duration-200 underline'
                          >
                            Associação Abacates do Brasil
                          </a>
                          .
                        </>
                      )}
                      {value.title === 'Associates' && (
                        <>
                          Avoprime is associated with the{' '}
                          <a
                            href='https://abacatesdobrasil.org.br/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-avoprime-primary hover:text-avoprime-secondary transition-colors duration-200 underline'
                          >
                            Brazilian Avocado Association
                          </a>
                          .
                        </>
                      )}
                      {value.title === 'Asociados' && (
                        <>
                          Avoprime está asociada con la{' '}
                          <a
                            href='https://abacatesdobrasil.org.br/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-avoprime-primary hover:text-avoprime-secondary transition-colors duration-200 underline'
                          >
                            Asociación Brasileña de Aguacates
                          </a>
                          .
                        </>
                      )}
                    </>
                  ) : (
                    value.description
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* International Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className='mb-8'
        >
          <div className='text-center mb-10'>
            <h3 className='text-3xl sm:text-4xl font-eras font-bold text-avoprime-secondary mb-3'>
              {t('aboutCompany.certifications.title')}
            </h3>
            <div className='w-12 h-0.5 bg-avoprime-secondary mx-auto mb-5' />
            <p className='text-sm sm:text-base font-montserrat text-gray-500 max-w-2xl mx-auto leading-relaxed'>
              {t('aboutCompany.certifications.subtitle')}
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            className='grid grid-cols-1 lg:grid-cols-2 gap-6'
          >
            {certifications.map((cert) => (
              <motion.div
                key={cert.badge}
                variants={itemVariants}
                className='bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-gray-100/80'
              >
                <div className='flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-start'>
                  <div className='flex-shrink-0 flex items-center justify-center sm:self-center'>
                    <img
                      src='/globalgap-logo.svg'
                      alt='GLOBALG.A.P.'
                      className='w-32 sm:w-36 h-auto object-contain'
                    />
                  </div>

                  <div className='flex-1 min-w-0 w-full'>
                    <div className='flex flex-wrap items-center gap-2.5 mb-2'>
                      <span className='inline-block bg-[#e8f3d8] text-avoprime-secondary text-[11px] font-eras font-bold tracking-wide px-3 py-1 rounded-full'>
                        {cert.badge}
                      </span>
                      <h4 className='text-lg sm:text-xl font-eras font-bold text-avoprime-secondary leading-tight'>
                        {cert.title}
                      </h4>
                    </div>
                    <p className='text-[11px] font-montserrat font-bold tracking-[0.14em] text-avoprime-primary uppercase mb-3'>
                      {cert.subtitle}
                    </p>
                    <p className='text-gray-600 font-montserrat text-sm leading-relaxed mb-4'>
                      {cert.description}
                    </p>
                    <ul className='space-y-2'>
                      {cert.features.map((feature) => (
                        <li key={feature} className='flex items-center gap-2.5'>
                          <span className='w-1.5 h-1.5 rounded-full bg-avoprime-primary flex-shrink-0' />
                          <span className='text-sm font-montserrat text-gray-700'>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutCompany;
